import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { userInfo } from 'api/graphQL/reactiveVars/user'
import { reactiveWorkspaceActions, setReactiveWorkspaceActions } from 'api/graphQL/reactiveVars/workspaceActions'
import {
    buildNodesWithInsertedPill,
    splitAnnotatedElementsAtOffset,
} from 'components/WorkspaceEditor/features/pillSelection/utils/optionGroupHelpers'
import { buildUOMHandlerForNewPill } from 'components/WorkspaceEditor/features/uomInsert'
import { TextElement } from 'components/WorkspaceEditor/services/TextElementParser'
import { ContentItemViewNodesType } from 'components/WorkspaceEditor/types/contentItemViewNode.type'
import { getBaseElementId } from 'components/WorkspaceEditor/utils'
import { getContentItemViewNodeByElementId } from 'components/WorkspaceEditor/utils/contentItemViewNodesUtils'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { MutableRefObject, useCallback, useLayoutEffect, useRef } from 'react'
import { v4 as newGuid } from 'uuid'
import { useWorkspace2Store } from '../../../Workspace2Store'

export { splitAnnotatedElementsAtOffset }

interface InsertPillRefs {
    textElement: TextContentElement
    setUnsavedData: (data: string) => void
    updateTreeViewNodes: (nodes: Partial<ContentItemViewNodesType>[]) => void
    debouncedOnChange: { cancel: () => void }
    deactivateCKEditor: () => void
    processEditorContent: (html: string) => TextElement[]
}

interface UseInsertPillAtCursorParams extends InsertPillRefs {
    editorRef: MutableRefObject<any>
    skipNextBlurRef: MutableRefObject<boolean>
}

/**
 * Encapsulates the "insert pill at cursor" action: reads cursor state from the
 * editor, delegates pure tree-building to buildNodesWithInsertedPill, then
 * applies the result back to the workspace store.
 */
export const useInsertPillAtCursor = ({
    editorRef,
    skipNextBlurRef,
    textElement,
    setUnsavedData,
    updateTreeViewNodes,
    debouncedOnChange,
    deactivateCKEditor,
    processEditorContent,
}: UseInsertPillAtCursorParams): (() => void) => {
    const pillInsertionRefs = useRef<InsertPillRefs>({
        textElement,
        setUnsavedData,
        updateTreeViewNodes,
        debouncedOnChange,
        deactivateCKEditor,
        processEditorContent,
    })
    useLayoutEffect(() => {
        pillInsertionRefs.current = {
            textElement,
            setUnsavedData,
            updateTreeViewNodes,
            debouncedOnChange,
            deactivateCKEditor,
            processEditorContent,
        }
    }, [
        textElement,
        setUnsavedData,
        updateTreeViewNodes,
        debouncedOnChange,
        deactivateCKEditor,
        processEditorContent,
    ])

    return useCallback(() => {
        const editor = editorRef.current
        if (!editor) return

        const refs = pillInsertionRefs.current
        const textEl = refs.textElement
        if (!textEl.id) return

        refs.debouncedOnChange.cancel()
        skipNextBlurRef.current = true
        refs.deactivateCKEditor()

        try {
            // ── Gather impure state from editor ────────────────────────────
            const selection = editor.model.document.selection
            const firstPos = selection.getFirstPosition()
            const offset = firstPos?.offset ?? 0
            const block = firstPos?.parent

            let fullText = ''
            if (block) {
                for (const child of Array.from(block.getChildren())) {
                    const c = (child as unknown) as {
                        is(type: string): boolean
                        data: string
                    }
                    if (c.is('$text') || c.is('$textProxy')) fullText += c.data
                }
            }

            const beforeText = fullText.substring(0, offset)
            const afterText = fullText.substring(offset)
            const elementId = getBaseElementId(textEl.id) ?? textEl.id
            const {
                contentItemViewNodes: currentNodes,
                actions,
            } = useWorkspace2Store.getState()
            const paragraphNode = getContentItemViewNodeByElementId(
                currentNodes,
                elementId
            )
            const annotated = refs.processEditorContent(editor.getData())
            const newSelectionOptionId = newGuid()
            const authoredUserId = userInfo()?.currentUserInfo?.kpUserId ?? ''

            // Strip annotation-only fields before passing as textElBase
            const {
                optionItemState: _ois,
                changeType: _ct,
                authoredUserId: _aui,
                placeholderText: _pt,
                ...textElBase
            } = (textEl as unknown) as TextContentElement & {
                authoredUserId?: string
            }

            // ── Pure tree transform ─────────────────────────────────────────
            const updatedNodes = buildNodesWithInsertedPill({
                currentNodes,
                paragraphNode,
                elementId,
                textElBase,
                beforeText,
                afterText,
                offset,
                annotated,
                newSelectionOptionId,
                authoredUserId,
            })

            // ── Apply side effects ──────────────────────────────────────────
            refs.updateTreeViewNodes(updatedNodes)
            refs.setUnsavedData('')
            setReactiveWorkspaceActions({
                isInsertPill: false,
                isInsertText: false,
            })
            actions.setTextContentElementToAdd({ id: newSelectionOptionId })

            // Capture nodes now (before any async store reset) for UOM handler
            const capturedNodes = useWorkspace2Store.getState()
                .contentItemViewNodes
            let capturedOgId: string | null = null
            for (const n of capturedNodes) {
                const els = (n as any).elements ?? []
                const og = els.find(
                    (el: any) =>
                        el.type === NODE_TYPES.OptionGroup &&
                        el.elements?.some(
                            (c: any) => c.id === newSelectionOptionId
                        )
                )
                if (og) {
                    capturedOgId = og.id
                    break
                }
            }

            const { insertPillRequirements: currentPillReqs } = reactiveWorkspaceActions()
            setReactiveWorkspaceActions({
                isInsertUOM: true,
                omWorkspaceInsertUOMHandler: buildUOMHandlerForNewPill(
                    capturedNodes,
                    capturedOgId,
                    newSelectionOptionId,
                    authoredUserId,
                    pillInsertionRefs,
                    skipNextBlurRef
                ),
                insertPillRequirements: {
                    ...currentPillReqs,
                    variant: NODE_TYPES.uomInPill,
                },
            })
        } catch {
            // skipNextBlurRef is cleared by the unmount-triggered blur
        }
    }, [editorRef, skipNextBlurRef])
}
